import { Match, Option } from 'effect';
import * as Atom from 'effect/unstable/reactivity/Atom';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useCallback, useContext, useEffect, useSyncExternalStore } from 'react';

import { AnnouncementContext } from './AnnouncementProvider';
import { initialAnnouncementSnapshot } from '@vs-shop/engine/atom/announcements/announcementAtoms';
import type { AnnouncementGroup } from '@vs-shop/engine/service/announcements/Announcements';
import type { Announcement } from '@vs-shop/engine/service/announcements/Announcement';

export function useAnnouncement(group: AnnouncementGroup, messages: readonly Announcement[]) {
  const scope = Option.getOrThrowWith(
    useContext(AnnouncementContext),
    () => new Error('AnnouncementProvider is missing.')
  );
  const { registry, atoms } = scope;
  const subscribe = useCallback(
    (notify: () => void) => registry.subscribe(atoms.snapshot, notify),
    [registry, atoms]
  );
  const read = useCallback(() => registry.get(atoms.snapshot), [registry, atoms]);
  const readServer = useCallback(
    () => Atom.getServerValue(atoms.snapshot, registry),
    [registry, atoms]
  );
  const result = useSyncExternalStore(subscribe, read, readServer);
  const state = AsyncResult.match(result, {
    onInitial: () => initialAnnouncementSnapshot,
    onFailure: () => initialAnnouncementSnapshot,
    onSuccess: ({ value }) => value,
  });

  useEffect(() => {
    registry.set(atoms.setMessages, { group, messages });
  }, [registry, atoms, group, messages]);

  const dismiss = useCallback(() => {
    const current = Match.value(group).pipe(
      Match.when('banner', () => state.banner),
      Match.when('base', () => state.base),
      Match.exhaustive
    );
    Option.match(current.pipe(Option.flatMap((message) => Option.fromNullishOr(message.id))), {
      onNone: () => undefined,
      onSome: (id) => registry.set(atoms.dismiss, String(id)),
    });
  }, [registry, atoms, group, state]);

  return {
    message: Match.value(group).pipe(
      Match.when('banner', () => state.banner),
      Match.when('base', () => state.base),
      Match.exhaustive
    ),
    dismiss,
  };
}
